import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ElementRef,
  ViewChild,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  HostListener,
  NgZone,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Apollo } from 'apollo-angular';
import gql from 'graphql-tag';

// ProseMirror imports (vanilla JS, no types needed - declare them)
declare var require: any;

const { Schema } = require('prosemirror-model');
const { schema: basicSchema } = require('prosemirror-schema-basic');
const { EditorState } = require('prosemirror-state');
const { EditorView } = require('prosemirror-view');
const { history, undo, redo } = require('prosemirror-history');
const { keymap } = require('prosemirror-keymap');
const { baseKeymap } = require('prosemirror-commands');

@Component({
  selector: 'app-prosemirror-editor',
  templateUrl: './prosemirror-editor.component.html',
  styleUrls: ['./prosemirror-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ProsemirrorEditorComponent),
      multi: true,
    },
  ],
})
export class ProsemirrorEditorComponent
  implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor {

  @ViewChild('editorContainer') editorContainer: ElementRef;
  @Input() placeholder = 'Write a review... Type @ to mention a team member';

  // mention dropdown state
  showSuggestions = false;
  suggestions: any[] = [];
  popupTop = '0px';
  popupLeft = '0px';

  private editorView: any;
  private schema: any;
  private mentionActive = false;
  private mentionFrom = 0;
  private searchTimeout: any;

  // ControlValueAccessor callbacks
  private onChange: (val: string) => void = () => {};
  private onTouched: () => void = () => {};

  private currentValue = '';

  constructor(private apollo: Apollo, private zone: NgZone, private elementRef: ElementRef) {}

  ngOnInit() {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.showSuggestions && !this.elementRef.nativeElement.contains(event.target)) {
      this.zone.run(() => {
        this.showSuggestions = false;
        this.suggestions = [];
      });
    }
  }

  ngAfterViewInit() {
    // Build custom schema with a 'mention' mark
    this.schema = new Schema({
      nodes: basicSchema.spec.nodes,
      marks: basicSchema.spec.marks.addToEnd('mention', {
        attrs: {
          id: {},
          label: {},
        },
        inclusive: false,
        toDOM: (mark) => [
          'span',
          {
            class: 'pm-mention',
            'data-mention-id': mark.attrs.id,
          },
          `@${mark.attrs.label}`,
        ],
        parseDOM: [
          {
            tag: 'span.pm-mention',
            getAttrs: (dom) => ({
              id: dom.getAttribute('data-mention-id'),
              label: dom.textContent.replace(/^@/, ''),
            }),
          },
        ],
      }),
    });

    const self = this;

    const initialDoc = this.schema.node('doc', null, [
      this.schema.node('paragraph', null, []),
    ]);

    const state = EditorState.create({
      doc: initialDoc,
      plugins: [
        history(),
        keymap({ 'Mod-z': undo, 'Mod-y': redo }),
        keymap(baseKeymap),
      ],
    });

    this.editorView = new EditorView(this.editorContainer.nativeElement, {
      state,
      dispatchTransaction(transaction) {
        const newState = self.editorView.state.apply(transaction);
        self.editorView.updateState(newState);

        if (transaction.docChanged) {
          const html = self.serializeToHtml(newState.doc);
          self.currentValue = html;
          self.zone.run(() => {
            self.onChange(html);
          });
        }

        // Check for @mention trigger
        self.zone.run(() => {
          self.checkForMentionTrigger(newState);
        });
      },
      handleKeyDown(view, event) {
        // Close suggestions on Escape
        if (event.key === 'Escape' && self.showSuggestions) {
          self.zone.run(() => { self.showSuggestions = false; });
          return true;
        }
        return false;
      },
    });

    // If we already have a value (e.g. edit mode), load it
    if (this.currentValue) {
      this.loadHtmlIntoEditor(this.currentValue);
    }
  }

  private checkForMentionTrigger(state: any) {
    const { from } = state.selection;
    const textBefore = state.doc.textBetween(
      Math.max(0, from - 50), from, '\n', '\0'
    );

    // Find the last '@' that hasn't been completed
    const atIndex = textBefore.lastIndexOf('@');
    if (atIndex === -1) {
      if (this.showSuggestions) {
        this.showSuggestions = false;
        this.suggestions = [];
      }
      this.mentionActive = false;
      return;
    }

    const query = textBefore.substring(atIndex + 1);
    // Only activate if query has no spaces (still typing the mention)
    if (query.indexOf(' ') > -1 && query.length > 30) {
      if (this.showSuggestions) {
        this.showSuggestions = false;
        this.suggestions = [];
      }
      this.mentionActive = false;
      return;
    }

    this.mentionActive = true;
    this.mentionFrom = from - query.length - 1; // position of '@'

    // Position the dropdown near the cursor
    // coordsAtPos returns viewport-relative coords; position:fixed needs no scroll offset
    const coords = this.editorView.coordsAtPos(from);
    this.popupTop = (coords.bottom + 4) + 'px';
    this.popupLeft = coords.left + 'px';

    // Debounce the search
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    this.searchTimeout = setTimeout(() => {
      this.zone.run(() => {
        this.fetchMentions(query);
      });
    }, 200);
  }

  fetchMentions(query: string) {
    this.apollo.query({
      query: gql`
        query GetMentions($query: String!) {
          mentionQuery(query: $query, limit: 10) {
            id
            type
            label
            image
          }
        }
      `,
      variables: { query },
    }).subscribe((result: any) => {
      this.zone.run(() => {
        if (result && result.data && result.data.mentionQuery) {
          this.suggestions = result.data.mentionQuery;
          this.showSuggestions = this.suggestions.length > 0;
        } else {
          this.suggestions = [];
          this.showSuggestions = false;
        }
      });
    }, () => {
      this.zone.run(() => {
        this.suggestions = [];
        this.showSuggestions = false;
      });
    });
  }

  selectMention(user: any) {
    if (!this.mentionActive || !this.editorView) { return; }

    const { state, dispatch } = this.editorView;
    const { from } = state.selection;
    const mentionMark = this.schema.mark('mention', {
      id: user.id,
      label: user.label,
    });

    // Replace from '@' up to current cursor with a mention mark + space
    const mentionText = this.schema.text(`@${user.label}`, [mentionMark]);
    const space = this.schema.text(' ');
    const tr = state.tr
      .replaceWith(this.mentionFrom, from, [mentionText, space])
      .removeStoredMark(this.schema.marks.mention);

    dispatch(tr);
    this.showSuggestions = false;
    this.suggestions = [];
    this.mentionActive = false;
    this.editorView.focus();
  }

  private serializeToHtml(doc: any): string {
    const div = document.createElement('div');
    const fragment = doc.content;
    let html = '';

    fragment.forEach((node) => {
      if (node.type.name === 'paragraph') {
        let paraHtml = '';
        node.forEach((inline) => {
          let text = inline.text || '';
          const mentionMark = inline.marks && inline.marks.find(m => m.type.name === 'mention');
          if (mentionMark) {
            paraHtml += `<span class="pm-mention" data-mention-id="${mentionMark.attrs.id}">@${mentionMark.attrs.label}</span>`;
          } else {
            // Escape HTML entities
            paraHtml += text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
          }
        });
        html += `<p>${paraHtml}</p>`;
      }
    });

    return html;
  }

  private loadHtmlIntoEditor(content: string) {
    if (!this.editorView || !content) { return; }

    const nodes = [];

    // Detect if content is plain text (no HTML tags) — handle legacy reviews
    const isHtml = /<[a-z][\s\S]*>/i.test(content);

    if (!isHtml) {
      // Plain text — split on newlines and create paragraph nodes
      const lines = content.split('\n');
      lines.forEach(line => {
        if (line.trim()) {
          nodes.push(this.schema.node('paragraph', null, [this.schema.text(line)]));
        } else {
          nodes.push(this.schema.node('paragraph', null, []));
        }
      });
    } else {
      // HTML content — parse and reconstruct
      const parser = new DOMParser();
      const domDoc = parser.parseFromString(`<div>${content}</div>`, 'text/html');
      const domNode = domDoc.body.firstChild;

      Array.from(domNode.childNodes).forEach((child: any) => {
        if (child.nodeName === 'P') {
          const inlines = [];
          Array.from(child.childNodes).forEach((inline: any) => {
            if (inline.nodeName === 'SPAN' && inline.classList.contains('pm-mention')) {
              const id = inline.getAttribute('data-mention-id');
              const label = inline.textContent.replace(/^@/, '');
              const mentionMark = this.schema.mark('mention', { id, label });
              inlines.push(this.schema.text(`@${label}`, [mentionMark]));
            } else if (inline.nodeType === 3 /* TEXT_NODE */ && inline.textContent) {
              inlines.push(this.schema.text(inline.textContent));
            }
          });
          if (inlines.length > 0) {
            nodes.push(this.schema.node('paragraph', null, inlines));
          } else {
            nodes.push(this.schema.node('paragraph', null, []));
          }
        } else if (child.nodeType === 3 && child.textContent && child.textContent.trim()) {
          // Raw text node at the top level (malformed HTML)
          nodes.push(this.schema.node('paragraph', null, [this.schema.text(child.textContent)]));
        }
      });
    }

    if (nodes.length === 0) {
      nodes.push(this.schema.node('paragraph', null, []));
    }

    const doc = this.schema.node('doc', null, nodes);
    const newState = EditorState.create({
      doc,
      plugins: this.editorView.state.plugins,
    });
    this.editorView.updateState(newState);
  }

  // ── ControlValueAccessor ──────────────────────────────────────────────────

  writeValue(value: string) {
    const newVal = value || '';
    // Avoid re-loading if content hasn't changed
    if (newVal === this.currentValue) { return; }
    this.currentValue = newVal;
    if (this.editorView) {
      this.loadHtmlIntoEditor(this.currentValue);
    }
  }

  registerOnChange(fn: (val: string) => void) {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  ngOnDestroy() {
    if (this.editorView) {
      this.editorView.destroy();
    }
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  }
}



// WEBPACK FOOTER //
// /Users/ugyen/Documents/Bhutan-office/my_bhutan/mybhutan-travel/src-admin/app/shared/prosemirror-editor/prosemirror-editor.component.ts